import { RecipeResponse, RecipesResponse } from '@/src/types/apps/gygRecipeTypes'
import { RecipeCountry } from '@/src/types/apps/recipeGuideTypes'
import { Recipe } from '@/src/views/components/recipe-mgmt/types'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

/** Safe for `rejectWithValue` when `error.response` is missing (e.g. network error, HTML 403 from load balancer). */
function rejectMessageFromAxios(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    const data = error.response?.data
    if (data === undefined || data === null) {
      if (status != null) {
        return `Request failed (${status}). The server did not return a JSON body — check permissions or that the recipe still exists.`
      }

      return error.message || 'Network error'
    }
    if (typeof data === 'string') {
      return data.length > 400 ? `${data.slice(0, 400)}…` : data
    }
    if (typeof data === 'object') {
      const o = data as Record<string, unknown>
      if (Array.isArray(o.message)) {
        return (o.message as string[]).filter(Boolean).join('; ')
      }
      if (typeof o.message === 'string') {
        return o.message
      }
      if (typeof o.error === 'string') {
        return o.error
      }
    }

    try {
      return JSON.stringify(data)
    } catch {
      return 'Request failed'
    }
  }
  if (error instanceof Error) {
    return error.message
  }

  return 'Request failed'
}

/** Structured reject payload for `uploadRecipeFile` (shared thunk; build form reads status for media retries). */
export type RecipeMediaUploadReject = {
  message: string
  status?: number
}

function rejectMediaUploadFromAxios(error: unknown): RecipeMediaUploadReject {
  const message = rejectMessageFromAxios(error)
  const status = axios.isAxiosError(error) ? error.response?.status : undefined

  return status != null ? { message, status } : { message }
}

/**
 * Prefer the API error body (Nest shape, etc.) for rejectWithValue so callers like
 * RecipeForm / CafeHolaRecipeForm can still read `payload.message`. Fall back to a
 * safe string only when `response` / `data` is missing (avoids throwing in the thunk).
 */
function rejectPayloadFromAxios(error: unknown): unknown {
  if (
    axios.isAxiosError(error) &&
    error.response &&
    error.response.data !== undefined &&
    error.response.data !== null
  ) {
    return error.response.data
  }

  return rejectMessageFromAxios(error)
}

function recipeErrorToString(payload: unknown): string {
  if (payload == null) {
    return 'Request failed'
  }
  if (typeof payload === 'string') {
    return payload
  }
  if (typeof payload === 'object' && payload !== null) {
    const o = payload as Record<string, unknown>
    if (Array.isArray(o.message)) {
      return (o.message as string[]).filter(Boolean).join('; ')
    }
    if (typeof o.message === 'string') {
      return o.message
    }
  }

  try {
    return JSON.stringify(payload)
  } catch {
    return 'Request failed'
  }
}

interface RecipeQueryOptions {
  take?: number
  skip?: number
  sortBy?: string
  filters: Record<string, any>
}

interface RecipeInitialState {
  recipes: Recipe[]
  recipe: RecipeResponse['data'] | null
  stats: {
    total: number
    published: number
    draft: number
  }
  loading: boolean
  meta: RecipesResponse['meta']
  error: string | null
  success: string | null
}
const initialState: RecipeInitialState = {
  recipes: [],
  recipe: null,

  // Initialize stats with default values
  stats: {
    total: 0,
    published: 0,
    draft: 0
  },
  loading: false,
  meta: {
    total: 0,
    pages: 0
  },
  error: null,
  success: null
}

/** Abort any in-flight list request so an older response cannot overwrite a newer sort/filter (Type=All + name sort). */
let getRecipesAbortController: AbortController | null = null

/**
 * Strip recipe `id` from POST/PUT bodies. Primary key must come from the URL on PUT
 * and from the DB on POST. A stale `id` in the JSON (Redux, drafts) can target the
 * wrong Recipes row and break RecipeProcedures FK constraints on test.
 */
export function recipePayloadWithoutRecipeId<T extends Record<string, unknown>>(data: T): Omit<T, 'id'> {
  const rest = { ...data }
  delete (rest as { id?: unknown }).id

  return rest as Omit<T, 'id'>
}

export const getRecipes = createAsyncThunk<
  RecipesResponse,
  RecipeQueryOptions,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipes/getRecipes', async ({ take = 10, skip = 0, sortBy = '-id', filters }, { rejectWithValue, extra }) => {
  getRecipesAbortController?.abort()
  getRecipesAbortController = new AbortController()
  const { signal } = getRecipesAbortController

  try {
    const response = await extra.httpClient.get(`recipes`, {
      params: { take, sortBy, filters, skip },
      signal
    })

    return response.data
  } catch (error: any) {
    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
      return rejectWithValue({ aborted: true })
    }

    return rejectWithValue(rejectPayloadFromAxios(error))
  }
})

export const createRecipe = createAsyncThunk<
  RecipeResponse,
  Partial<RecipesResponse['data'][0]>,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipes/createRecipe', async (recipe, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.post(
      'recipes',
      recipePayloadWithoutRecipeId(recipe as Record<string, unknown>)
    )

    return response.data
  } catch (error: unknown) {
    return rejectWithValue(rejectPayloadFromAxios(error))
  }
})

export const updateRecipe = createAsyncThunk<
  RecipeResponse,
  { id: number; data: Partial<RecipesResponse['data'][0]> },
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipes/updateRecipe', async ({ id, data }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.put(
      `recipes/${id}`,
      recipePayloadWithoutRecipeId(data as Record<string, unknown>)
    )

    return response.data
  } catch (error: unknown) {
    return rejectWithValue(rejectPayloadFromAxios(error))
  }
})

export const uploadRecipeFile = createAsyncThunk<
  RecipeResponse,
  {
    id: number
    file: File
    procedureId?: number
    procedureUniqueId?: string

    /** `RecipeIngredients.id` — required with mediaField `ingredientPortioning` */
    recipeIngredientId?: number
    mediaField: string
  },
  { rejectValue: RecipeMediaUploadReject; extra: { httpClient: any } }
>(
  'gygRecipes/uploadRecipeFile',
  async ({ id, file, procedureId, procedureUniqueId, recipeIngredientId, mediaField }, { rejectWithValue, extra }) => {
    try {
      const formData = new FormData()
      formData.append('mediaField', mediaField)
      if (procedureId) {
        formData.append('procedureId', procedureId.toString())
      }
      if (procedureUniqueId) {
        formData.append('procedureUniqueId', procedureUniqueId)
      }
      if (recipeIngredientId != null) {
        formData.append('recipeIngredientId', recipeIngredientId.toString())
      }
      formData.append('file', file)

      const response = await extra.httpClient.post(`recipes/${id}/media`, formData, {
        timeout: 120_000
      })

      return response.data
    } catch (error: unknown) {
      return rejectWithValue(rejectMediaUploadFromAxios(error))
    }
  }
)

export const getRecipe = createAsyncThunk<RecipeResponse, string, { rejectValue: any; extra: { httpClient: any } }>(
  'gygRecipes/getRecipe',
  async (id, { rejectWithValue, extra }) => {
    try {
      const response = await extra.httpClient.get(`recipes/${id}`)

      return response.data
    } catch (error: unknown) {
      return rejectWithValue(rejectPayloadFromAxios(error))
    }
  }
)

export const deleteRecipe = createAsyncThunk<{ id: string }, string, { rejectValue: any; extra: { httpClient: any } }>(
  'gygRecipes/deleteRecipe',
  async (id, { rejectWithValue, extra }) => {
    try {
      await extra.httpClient.delete(`recipes/${id}`)

      return { id }
    } catch (error: unknown) {
      return rejectWithValue(rejectPayloadFromAxios(error))
    }
  }
)

export const getRecipeStats = createAsyncThunk<
  any,
  { country: RecipeCountry },
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipes/getStats', async ({ country }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get('recipes/stats', {
      params: { country }
    })

    return response.data
  } catch (error: unknown) {
    return rejectWithValue(rejectPayloadFromAxios(error))
  }
})

const gygRecipeSlice = createSlice({
  name: 'gygRecipe',
  initialState,
  reducers: {
    clearGygRecipeSuccess(state) {
      state.success = null
    },
    clearGygRecipeError(state) {
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(getRecipes.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipes.fulfilled, (state, action) => {
      state.loading = false
      state.error = null
      state.success = null
      state.recipes = action.payload.data
      state.meta = action.payload.meta
    })
    builder.addCase(getRecipes.rejected, (state, action) => {
      state.loading = false
      const payload = action.payload as { aborted?: boolean } | undefined
      if (payload && typeof payload === 'object' && payload.aborted) {
        return
      }
      state.error = (action.payload as any) || 'Failed to fetch recipes'
    })
    builder.addCase(createRecipe.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(createRecipe.fulfilled, (state, action) => {
      state.loading = false
      state.recipes.unshift(action.payload.data)
      state.recipe = action.payload.data
      state.meta.total += 1
      state.success = 'Recipe created successfully'
    })
    builder.addCase(createRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = (action.payload as any) || 'Failed to create recipe'
    })

    builder.addCase(updateRecipe.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(updateRecipe.fulfilled, (state, action) => {
      state.loading = false
      state.recipe = action.payload.data
      state.success = 'Recipe updated successfully'
    })
    builder.addCase(updateRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = (action.payload as any) || 'Failed to update recipe'
    })
    builder.addCase(uploadRecipeFile.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(uploadRecipeFile.fulfilled, (state, action) => {
      state.loading = false
      const payload = action.payload as RecipeResponse | undefined
      if (payload?.data) {
        state.recipe = payload.data
      }
      state.success = 'File uploaded successfully'
    })
    builder.addCase(uploadRecipeFile.rejected, (state, action) => {
      state.loading = false
      state.error = recipeErrorToString(action.payload) || 'Failed to upload file'
    })

    builder.addCase(getRecipe.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipe.fulfilled, (state, action) => {
      state.loading = false

      // Optionally update or add the fetched recipe to the list
      const recipe = action.payload.data
      state.recipe = recipe
    })
    builder.addCase(getRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = (action.payload as any) || 'Failed to fetch recipe'
    })

    builder.addCase(deleteRecipe.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(deleteRecipe.fulfilled, (state, action) => {
      state.loading = false
      state.recipes = state.recipes.filter(r => r.id !== +action.payload.id)
      state.meta.total -= 1
      state.success = 'Recipe deleted successfully'
    })
    builder.addCase(deleteRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = (action.payload as any) || 'Failed to delete recipe'
    })

    builder.addCase(getRecipeStats.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipeStats.fulfilled, (state, action) => {
      state.loading = false

      state.stats = {
        total: action.payload.data.totalRecipes,
        published: action.payload.data.publishedRecipes,
        draft: action.payload.data.draftRecipes
      }
    })
    builder.addCase(getRecipeStats.rejected, (state, action) => {
      state.loading = false
      state.error = (action.payload as any) || 'Failed to fetch stats'
    })
  }
})

export const { clearGygRecipeSuccess, clearGygRecipeError } = gygRecipeSlice.actions
export default gygRecipeSlice.reducer
